/**
 * Page d'achat d'une SKILLCARD (migration 0304) — `/card/$cardId/acheter`.
 *
 * Trois façons de débloquer la carte : la carte seule, un pack (chapitre) qui
 * la contient, ou tout le programme de l'année. Prix calculés par la base
 * (grille éditable dans l'admin, cartes déjà possédées déduites). Le deck est
 * actif 1 an à compter de l'achat, et chaque achat repousse la date pour
 * TOUTES les cartes.
 *
 * Publique (enfant de rootRoute) : un visiteur voit les prix et se connecte
 * sur la page. Retour Stripe : `?purchase=<id>` → on attend le webhook puis on
 * renvoie vers `?return=` (l'exercice en cours, qui reprend sa tentative).
 *
 * Natif : achat intégré (palier Google Play / App Store, 0351), jamais de lien Stripe.
 */
import { publishProgress } from '@/lib/progress-sync';
import { useEffect, useState } from 'react';
import { useParams, useSearch } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { formatDeckDate, skillcardAccessLabel, stripMath } from '@jeraisonne/db/cards';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { canBuySkillcardsInApp } from '@/lib/skillcards/iap';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { setReturnTo } from '@/lib/return-to';
import {
  safeInternalPath,
  useMyDeck,
  useSkillcardOffers,
  useSkillcardPurchaseStatus,
} from '@/lib/skillcards/purchase';
import { SkillcardBuyHero } from '@/features/skillcards/skillcard-buy-hero';
import { SkillcardOffersPanel } from '@/features/skillcards/skillcard-offers-panel';
import { usePublicCard } from '@/lib/cards/api';

export function SkillcardBuyPage() {
  const { cardId } = useParams({ strict: false }) as { cardId: string };
  const search = useSearch({ strict: false }) as { return?: string; purchase?: string };
  const returnPath = safeInternalPath(search.return);
  const { session, profile } = useAuth();
  const userId = session?.user?.id ?? null;
  const offers = useSkillcardOffers(cardId, userId);
  const publicCard = usePublicCard(cardId);
  const [showLogin, setShowLogin] = useState(false);
  // App native : achat via Google Play / App Store (paliers, 0351).
  const native = !canBuySkillcardsInApp();
  // « Offrir » depuis la page de la carte : `#offrir` ouvre la carte cadeau.
  const [giftOpen] = useState(
    () => typeof window !== 'undefined' && window.location.hash === '#offrir',
  );

  useEffect(() => {
    if (offers.data) {
      document.title = `Débloquer ${stripMath(offers.data.card.label)} · JeRaisonne`;
    }
  }, [offers.data]);

  if (search.purchase) {
    return <PurchaseResult cardId={cardId} purchaseId={search.purchase} returnPath={returnPath} />;
  }

  const data = offers.data;
  const accessLabel = data ? skillcardAccessLabel(data.access.reason) : null;
  const signedIn = !!session && !!profile;

  return (
    <div className="native:pt-[calc(env(safe-area-inset-top)+0.75rem)] min-h-dvh px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-3 sm:py-8">
      <div className="mx-auto max-w-xl space-y-4">
        <a
          href={returnPath ?? `/card/${cardId}`}
          className="text-ink-500 hover:text-ink-800 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Retour
        </a>

        {offers.isLoading ? (
          <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
        ) : !data ? (
          <p className="text-ink-600 text-sm">Cette SKILLCARD n’est pas disponible.</p>
        ) : (
          <>
            {publicCard.data && (
              <SkillcardBuyHero
                title={publicCard.data.card.title}
                levels={publicCard.data.card.levels}
                colorTheme={publicCard.data.card.color_theme}
                creature={publicCard.data.card.creature}
                xpMax={publicCard.data.card.xp_max}
              />
            )}
            {/* La date du deck est dans « Ton deck » sous les offres (plus de doublon). */}
            {accessLabel && (
              <div className="bg-success-50 text-success-800 rounded-2xl p-4 text-sm">
                <p className="font-semibold">{accessLabel}.</p>
              </div>
            )}

            {native ? (
              <div className="bg-ink-50 text-ink-700 rounded-2xl p-4 text-sm">
                Les SKILLCARDS s’achètent sur le site jeraisonne.fr. Elles sont aussi incluses dans
                l’abonnement JeRaisonne.
              </div>
            ) : (
              <>
                {!signedIn && (
                  <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
                    <p className="text-ink-700 text-sm">
                      Connecte-toi d’abord pour pouvoir acheter : les cartes s’ajoutent à ton deck.
                    </p>
                    {showLogin ? (
                      <OtpAuthForm
                        renderHeader={() => (
                          <p className="text-ink-900 font-display text-base font-bold">
                            Connexion ou création de compte — tu reviendras sur cette page
                          </p>
                        )}
                      />
                    ) : (
                      <Button
                        fullWidth
                        onClick={() => {
                          setReturnTo(`/card/${cardId}/acheter`);
                          setShowLogin(true);
                        }}
                      >
                        Se connecter
                      </Button>
                    )}
                  </div>
                )}
                <SkillcardOffersPanel
                  cardId={cardId}
                  userId={userId}
                  signedIn={signedIn}
                  returnPath={returnPath}
                  giftOpen={giftOpen}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Retour de Stripe : attend le webhook, puis renvoie vers l'exercice / la carte.
 * Partagé avec la page d'achat d'un pack (`retryHref`, `target`, `doneLabel`).
 */
export function PurchaseResult({
  cardId,
  purchaseId,
  returnPath,
  retryHref,
  target: targetOverride,
  doneLabel,
}: {
  cardId: string;
  purchaseId: string;
  returnPath: string | null;
  retryHref?: string;
  target?: string;
  doneLabel?: string;
}) {
  const { session } = useAuth();
  const qc = useQueryClient();
  const status = useSkillcardPurchaseStatus(session ? purchaseId : null);
  const deck = useMyDeck(session?.user?.id ?? null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 60_000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (status.data === 'paid') publishProgress('cards-granted', qc);
  }, [status.data, qc]);

  const target =
    targetOverride ??
    (returnPath
      ? `${returnPath}${returnPath.includes('?') ? '&' : '?'}pass_success=1`
      : `/card/${cardId}`);

  return (
    <div className="native:pt-[calc(env(safe-area-inset-top)+0.75rem)] flex min-h-dvh items-center px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)]">
      <div className="mx-auto w-full max-w-md space-y-4 text-center">
        {status.data === 'paid' ? (
          <>
            <CheckCircle2 className="text-success-500 mx-auto h-12 w-12" aria-hidden />
            <h1 className="font-display text-ink-900 text-2xl font-extrabold">C’est débloqué !</h1>
            {deck.data && (
              <p className="text-ink-600 text-sm">
                Ton deck est actif jusqu’au <strong>{formatDeckDate(deck.data)}</strong>.
              </p>
            )}
            <Button
              fullWidth
              // replace : la page « C'est débloqué » sort de l'historique.
              onClick={() => window.location.replace(target)}
            >
              {doneLabel ?? (returnPath ? 'Reprendre l’exercice' : 'Voir la carte')}
            </Button>
          </>
        ) : status.data === 'failed' ? (
          <>
            <h1 className="font-display text-ink-900 text-xl font-extrabold">
              Le paiement n’a pas abouti
            </h1>
            <a
              href={retryHref ?? `/card/${cardId}/acheter`}
              className="text-primary-600 inline-flex min-h-[2.75rem] items-center font-semibold"
            >
              Revenir aux offres
            </a>
          </>
        ) : (
          <>
            <Loader2 className="text-primary-500 mx-auto h-10 w-10 animate-spin" aria-hidden />
            <h1 className="font-display text-ink-900 text-xl font-extrabold">
              On ajoute tes cartes à ton deck…
            </h1>
            {timedOut && (
              <p className="text-ink-600 text-sm">
                C’est plus long que prévu. Ton paiement est bien pris en compte : recharge la page
                dans un instant.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
